import { useEffect, useMemo, useRef, useState } from 'react';
import { NJButton, NJCheckbox, NJText } from '@engie-group/fluid-design-system-react';
import type { CellClickedEvent, ColDef, GridApi, ICellRendererParams, RowClassParams } from 'ag-grid-community';
import DataTable from '@shared/DataTable';
import { UMSelect } from '../../urbanmap/_components';
import { buildingsByIds, YEARS } from './engine';
import type { Plan } from './types';

interface Row { id: string; address: string; usage: string; heat: number | null; year: string }
export function PhaseTable({ plan, canWrite, focusId, focusRequest, selectedIds, onFocus, onSelect, onYear }: {
  plan: Plan; canWrite: boolean; focusId: string; focusRequest: number; selectedIds: string[];
  onFocus: (id: string) => void; onSelect: (ids: string[]) => void; onYear: (id: string, year: string) => void;
}) {
  const api = useRef<GridApi<Row>>();
  const focusRef = useRef(focusId);
  focusRef.current = focusId;
  const onFocusRef = useRef(onFocus);
  onFocusRef.current = onFocus;
  const [ready, setReady] = useState(0);
  const [notice, setNotice] = useState('');
  const rows = useMemo(() => {
    const data = new Map(buildingsByIds(plan.buildingIds ?? []).map((building) => [building.id, building]));
    return (plan.buildingIds ?? []).map((id): Row => ({
      id, address: data.get(id)?.address ?? id, usage: data.get(id)?.usage ?? 'Non renseigné',
      heat: data.get(id)?.heatKnown ? data.get(id)!.heat : null, year: plan.buildingPhases?.[id] ?? YEARS[0],
    }));
  }, [plan.buildingIds, plan.buildingPhases]);
  useEffect(() => {
    const grid = api.current;
    if (!grid || grid.isDestroyed() || !focusId) return;
    let node: ReturnType<typeof grid.getRowNode>;
    grid.forEachNode((row) => { if (row.data?.id === focusId) node = row; });
    if (!node) return;
    if (node.rowIndex === null) {
      grid.setFilterModel(null);
      setNotice('Filtres réinitialisés pour afficher le bâtiment sélectionné sur la carte.');
    } else setNotice('');
    grid.ensureNodeVisible(node, 'middle');
    if (node.rowIndex !== null) grid.setFocusedCell(node.rowIndex, 'address');
    grid.redrawRows();
  }, [focusId, focusRequest, ready, rows]);
  const columns: ColDef<Row>[] = [
    { headerName: 'Choix', colId: 'choice', minWidth: 65, maxWidth: 75, filter: false, sortable: false,
      cellRenderer: (params: ICellRendererParams<Row>) => params.data && <NJCheckbox
        label="" aria-label={`Choisir ${params.data.address}`}
        isChecked={selectedIds.includes(params.data.id)}
        onChange={(_, checked) => params.data && onSelect(checked
          ? [...new Set([...selectedIds, params.data.id])] : selectedIds.filter((id) => id !== params.data?.id))} /> },
    { field: 'address', headerName: 'Adresse', minWidth: 165, flex: 2,
      cellRenderer: (params: ICellRendererParams<Row>) => params.data && <NJButton
        label={params.data.address} title={params.data.address} variant="secondary" emphasis="minimal" scale="sm"
        onClick={() => params.data && onFocus(params.data.id)} /> },
    { field: 'usage', headerName: 'Usage', minWidth: 100 },
    { field: 'heat', headerName: 'MWh/an · chaleur', minWidth: 110, filter: 'agNumberColumnFilter',
      valueFormatter: ({ value }) => value === null ? 'Non renseignée' : Number(value).toLocaleString('fr-FR') },
    { field: 'year', headerName: 'Année', minWidth: 110,
      cellRenderer: (params: ICellRendererParams<Row>) => params.data && <fieldset disabled={!canWrite} style={{ border: 0, margin: 0, padding: 0 }}>
        <UMSelect value={params.data.year} ariaLabel={`Année de ${params.data.id}`}
          options={YEARS.map((year) => ({ value: year, label: year }))}
          onChange={(year) => params.data && onYear(params.data.id, year)} />
      </fieldset> },
    { field: 'id', headerName: 'Identifiant', minWidth: 140 },
  ];
  return <div>
    <NJText scale="xs">Colonnes triables et filtrables. Défilez horizontalement pour voir l’identifiant. Consommations chaleur estimées du périmètre.</NJText>
    {notice && <NJText role="status" scale="xs">{notice}</NJText>}
    <div className="np-phase-grid" style={{ height: 350, overflow: 'hidden' }}>
      <style>{'.np-phase-grid > div { height: 100% !important; } .np-phase-grid .ag-row:focus-within { outline: 2px solid var(--nj-semantic-color-border-brand-primary-default); }'}</style>
      <DataTable data={rows} header={columns} testId="phase-buildings" verticalScroll
        defaultColDef={{ flex: 1, minWidth: 100, sortable: true, filter: true, floatingFilter: true, resizable: true }}
        onGridReady={(event) => {
          api.current = event.api;
          event.api.setGridOption('getRowStyle', (params: RowClassParams<Row>) => params.data?.id === focusRef.current
            ? { background: 'var(--nj-core-color-teal-100, #e3f4f2)' } : undefined);
          event.api.addEventListener('cellClicked', (params: CellClickedEvent<Row>) => {
            if (params.data && !['choice', 'year', 'address'].includes(params.column.getColId())) onFocusRef.current(params.data.id);
          });
          setReady((value) => value + 1);
        }} />
    </div>
  </div>;
}
